import { Link } from 'react-router-dom';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths } from 'date-fns';
import { Subscription } from '../../types';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface RenewalCalendarProps {
  renewals: Subscription[];
}

export default function RenewalCalendar({ renewals }: RenewalCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Group renewals by date (filter out ones without valid dates)
  const renewalsByDate = renewals
    .filter(r => r.renewalDate)
    .reduce((acc, renewal) => {
      const date = format(parseISO(renewal.renewalDate), 'yyyy-MM-dd');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(renewal);
      return acc;
    }, {} as Record<string, Subscription[]>);

  const prevMonth = () => setCurrentDate(addMonths(currentDate, -1));
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground">
            Renewal Calendar
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={prevMonth}
              className="p-1 rounded hover:bg-accent"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-sm font-medium text-foreground min-w-[120px] text-center">
              {format(currentDate, 'MMMM yyyy')}
            </span>
            <button
              onClick={nextMonth}
              className="p-1 rounded hover:bg-accent"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-muted-foreground py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before month start */}
          {Array.from({ length: monthStart.getDay() }).map((_, i) => (
            <div key={`empty-start-${i}`} className="p-2" />
          ))}

          {days.map((day) => {
            const dateKey = format(day, 'yyyy-MM-dd');
            const dayRenewals = renewalsByDate[dateKey] || [];
            const hasRenewals = dayRenewals.length > 0;

            return (
              <div
                key={dateKey}
                className={`p-2 text-center rounded-lg relative ${
                  isToday(day)
                    ? 'bg-primary/10 font-bold'
                    : hasRenewals
                    ? 'bg-amber-500/5 hover:bg-amber-500/10'
                    : 'hover:bg-accent'
                }`}
              >
                <span
                  className={`text-sm ${
                    isToday(day)
                      ? 'text-primary'
                      : 'text-foreground'
                  }`}
                >
                  {format(day, 'd')}
                </span>
                {hasRenewals && (
                  <div className="mt-1">
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-amber-800 bg-amber-200 dark:bg-amber-700 dark:text-amber-100 rounded-full">
                      {dayRenewals.length}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Renewals in selected month */}
        {(() => {
          const monthRenewals = renewals.filter(r => {
            if (!r.renewalDate) return false;
            const renewalDate = parseISO(r.renewalDate);
            return renewalDate >= monthStart && renewalDate <= monthEnd;
          }).sort((a, b) => parseISO(a.renewalDate).getTime() - parseISO(b.renewalDate).getTime());

          return monthRenewals.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Renewals in {format(currentDate, 'MMMM yyyy')} ({monthRenewals.length})
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {monthRenewals.map((renewal) => (
                <Link
                  key={renewal.id}
                  to={`/subscriptions/${renewal.id}`}
                  className="flex items-center justify-between p-2 rounded hover:bg-accent"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground truncate">
                      {renewal.productName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {renewal.clientName}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground ml-2">
                    {renewal.renewalDate ? format(parseISO(renewal.renewalDate), 'MMM d') : 'N/A'}
                  </span>
                </Link>
              ))}
            </div>
            <Link
              to="/subscriptions"
              className="block mt-3 text-sm text-center text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              View all subscriptions
            </Link>
          </div>
        );
        })()}
      </div>
    </div>
  );
}
